<template>
    <div>
        <div class="box">
            <div class="deal-form-title mb-10">
                <span v-if="isPromotion"> Promotion </span>
                <span v-else>
                    <span v-if="proposal.deal_status === 'current'">We are currently in</span>
                    <span v-else>Currently viewing</span>
                    {{ deal.duration_interval }} {{ proposal.deal_position }} of {{ deal.duration_length }}
                </span>
            </div>

            <span v-if="isPromotion"> We are currently scheduled to go live on {{ proposal.end_date_readable }}. </span>
            <span v-else>
                This {{ deal.duration_interval }} started on {{ pShortDate(proposal.start_date) }} and will end on
                {{ pShortDate(proposal.end_date) }}.
            </span>

            <br />

            <slot />
        </div>

        <!--        <div class="box" v-if="proposal.has_payment && proposal.price">-->
        <!--            <div class="deal-form-title mb-10">-->
        <!--                Your payment for this {{ interval }}.-->
        <!--            </div>-->

        <!--            You will receive a payment of-->
        <!--            <span class="has-text-success text-bold">-->
        <!--                <money-display :value="proposal.price" :currency="deal.brand.currency" />-->
        <!--            </span>-->
        <!--        </div>-->

        <div
            v-if="(deal.affiliate_link_id || deal.discount_code_group_id) && deal.show_revenue_tracking_information"
            class="box"
        >
            <div class="deal-form-title mb-10">
                Your
                <span v-if="deal.discount_code_group_id">discount code</span>
                <span v-if="deal.affiliate_link_id && deal.discount_code_group_id">and</span>
                <span v-if="deal.affiliate_link_id">shareable link</span>
            </div>
            <affiliate-compensation :campaign="deal" />
        </div>

        <div v-if="hasProductCompensation" class="box">
            <div v-if="!hasProductsAvailable">
                <blank-state :inner="true" size="small" icon="shopping-basket">
                    <template #title>
                        <div>Your Products</div>
                    </template>
                    <template #content>
                        <div>
                            Your products have not been configured for this {{ interval }}.
                            <br />
                            Please check back soon.
                        </div>
                    </template>
                </blank-state>
            </div>

            <div v-else>
                <loader v-if="requestingProducts" />

                <div v-else>
                    <div v-for="selection in productSelections" :key="selection.id">
                        <div v-if="editingProducts" class="is-clearfix">
                            <products-compensation
                                :campaign="deal"
                                :proposal="proposal"
                                :products="selection.products"
                                @change="handleProductsChange"
                            />

                            <div class="mt-20 is-pulled-right">
                                <button
                                    class="button is-small is-success"
                                    :class="{ 'is-loading': savingProducts }"
                                    :disabled="!selectedProducts.length"
                                    @click="handleProductsSave(selection.id)"
                                >
                                    Save Products
                                </button>

                                <button
                                    class="button is-small is-light is-cancel"
                                    :disabled="savingProducts"
                                    @click="handleProductsCancel"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>

                        <products-compensation-selected
                            v-else
                            :campaign="deal"
                            :proposal="proposal"
                            :products="selection.products"
                            @edit="handleProductsEdit"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div v-if="deal.payables.length" class="box">
            <div class="deal-form-title mb-10">Payment Schedule</div>

            <table class="table is-compact is-fullwidth deal-payment-schedule-table">
                <tr>
                    <th>Schedule</th>
                    <th>Payment</th>
                </tr>

                <tr v-for="(item, index) in paymentSchedule" :key="item.label + index">
                    <td>
                        {{ item.label }}
                    </td>

                    <td>
                        <money-display :value="item.amount" :currency="deal.brand.currency" />
                    </td>
                </tr>
            </table>
        </div>

        <div v-if="hasProductCompensation && hasProductsAvailable" class="box">
            <div class="mt-25">
                <div class="mb-15 text-bold">Please make sure your shipping address is up to date</div>

                <address-info ref="address" :in-settings="true" />
            </div>
        </div>

        <div v-if="!isPromotion" class="box">
            <div class="deal-form-title mb-15">Please make sure your social networks are connected</div>

            <network-manager
                :contact-id="contactId"
                context="profile"
                :networks="networks"
                :contact-networks="contactNetworks"
            />
        </div>

        <div class="box">
            <div class="deal-form-title mb-15">Your deliverables for this {{ interval }}</div>
            <job-tasks :campaign="deal" :jobs="jobs" :deliverable="hasStarted" />
        </div>
        <div v-if="deal.allow_upload" class="box">
            <div class="deal-form-title mb-15">Your uploaded files for this {{ interval }}</div>
            <proposal-files :proposal="proposal" />
        </div>
        <div v-if="throughoutJobs.length" class="box">
            <div class="deal-form-title mb-15">Your deliverables throughout this partnership</div>
            <job-tasks :campaign="deal" :jobs="throughoutJobs" :deliverable="true" />
        </div>
    </div>
</template>

<script>
    import find from 'lodash-es/find';
    import uniqBy from 'lodash-es/uniqBy';
    import capitalize from 'lodash-es/capitalize';
    import MoneyDisplay from '../../../interface/elements/MoneyDisplay.vue';
    import JobTasks from '../../job/JobTasks.vue';
    import ProposalFiles from '../../job/ProposalFiles.vue';
    import ProductsCompensation from '../../campaign/proposal/compensation/ProductsCompensation.vue';
    import ProductsCompensationSelected from '../../campaign/proposal/compensation/ProductsCompensationSelected.vue';
    import AddressInfo from '../../settings/profile/AddressInfo.vue';
    import AffiliateCompensation from '../../campaign/proposal/compensation/AffiliateCompensation.vue';
    import NetworkManager from '../../../../../app-v2/components/data-views/networks/partials/NetworkManager.vue';
    import { defineComponent } from 'vue-demi';
    import { platformShortDate } from '../../../../helpers/utils';

    export default defineComponent({
        /**
         * The component name
         *
         * @type {String}
         */
        name: 'DealProposalListRow',

        /**
         * The child components
         *
         * @type {Object}
         */
        components: {
            NetworkManager,
            AffiliateCompensation,
            AddressInfo,
            MoneyDisplay,
            JobTasks,
            ProposalFiles,
            ProductsCompensation,
            ProductsCompensationSelected
        },

        /**
         * The component properties
         */
        props: {
            /**
             * The deal
             */
            deal: {
                type: Object,
                required: true
            },

            /**
             * The proposal
             */
            proposal: {
                type: Object,
                required: true
            }
        },
        watch: {
            /**
             * Watch the proposal
             */
            proposal: {
                immediate: true,
                handler() {
                    this.$nextTick(() => {
                        if (this.hasProductCompensation && this.hasProductsAvailable) {
                            this.requestProductSelections();
                        }
                    });
                }
            }
        },

        /**
         * The component data
         *
         * @returns {Object}
         */
        data() {
            return {
                /**
                 * Proposal's product selections
                 */
                productSelections: [],

                /**
                 * Selected products
                 *
                 * @type {Array}
                 */
                selectedProducts: [],

                /**
                 * Whether or not the products are being loaded
                 *
                 * @type {Boolean}
                 */
                requestingProducts: true,

                /**
                 * Whether or not the products are being edited
                 *
                 * @type {Boolean}
                 */
                editingProducts: false,

                /**
                 * Whether or not the products are being saved
                 *
                 * @type {Boolean}
                 */
                savingProducts: false
            };
        },

        /**
         * The computed properties
         *
         * @type {Object}
         */
        computed: {
            /**
             * Array of scheduled payments
             *
             * @returns {Array}
             */
            paymentSchedule() {
                const schedule = [];

                const upfrontAmount = this.deal.payables
                    .filter((payable) => 'upfront' === payable.subtype)
                    .reduce((total, payable) => total + payable.amount, 0);
                if (upfrontAmount) {
                    schedule.push({
                        label: 'Up Front',
                        amount: upfrontAmount
                    });
                }

                this.deal.proposals.forEach((proposal) => {
                    const amount = this.deal.payables
                        .filter((payable) => 'proposal' === payable.item_type && proposal.id === payable.item_id)
                        .reduce((total, payable) => total + payable.amount, 0);
                    if (amount) {
                        schedule.push({
                            label: `${capitalize(this.deal.duration_interval)} ${proposal.deal_position}`,
                            amount
                        });
                    }
                });

                const uponCompletionAmount = this.deal.payables
                    .filter((payable) => 'completion' === payable.subtype)
                    .reduce((total, payable) => total + payable.amount, 0);
                if (uponCompletionAmount) {
                    schedule.push({
                        label: 'Upon Completion',
                        amount: uponCompletionAmount
                    });
                }

                return schedule;
            },

            /**
             * The job proposal
             *
             * @returns {object}
             */
            jobs() {
                return this.$root.getJobs(this.proposal.id);
            },
            /**
             * The jobs to be delivered throughout the partnership
             */
            throughoutJobs() {
                return this.$root.getJobs(null).filter((job) => {
                    return job.campaign_id === this.deal.id && job.is_global === 1;
                });
            },
            /**
             * The interval
             *
             * @returns {string}
             */
            interval() {
                return this.deal.partnership_type === 'promotion' ? 'partnership' : this.deal.duration_interval;
            },

            /**
             * The job proposal
             *
             * @returns {array}
             */

            networks() {
                return uniqBy(
                    [...this.jobs, ...this.throughoutJobs]
                        .filter((job) => job.task && job.task.network)
                        .map((job) => job.task.network),
                    'slug'
                );
            },

            /**
             * Whether or not the proposal has started
             *
             * @returns {Boolean}
             */
            hasStarted() {
                return 'not_started' !== this.proposal.deal_status;
            },

            /**
             * Whether this is a promotion
             *
             * @returns {boolean}
             */
            isPromotion() {
                return this.deal.partnership_type === 'promotion';
            },

            /**
             * Whether or not the proposal has product compensation
             *
             * @returns {Boolean}
             */
            hasProductCompensation() {
                return this.proposal.has_product && this.deal.compensation.indexOf('product') !== -1;
            },

            /**
             * Whether there are products left to show
             *
             * @returns {Boolean}
             */
            hasProductsAvailable() {
                if (this.proposal) {
                    return (
                        this.proposal.has_configured_products ||
                        (this.proposal.compensation_settings && this.proposal.compensation_settings.use_checkout_url)
                    );
                }

                return this.campaign.has_configured_products;
            },

            contactId() {
                return this.$root.contact_id;
            },
            contactNetworks() {
                return this.$root.networks;
            }
        },

        /**
         * The component methods
         *
         * @type {Object}
         */
        methods: {
            pShortDate(value) {
                return platformShortDate(value, null, true)
            },
            /**
             * Load selected products
             */
            requestProductSelections() {
                this.requestingProducts = true;

                this.$http
                    .get(`/campaigns/proposal/${this.proposal.id}/products/selections`)
                    .then(this.receiveProductSelections)
                    .catch(() => this.$notifyError('Could not load products, please try again or contact support'))
                    .then(() => (this.requestingProducts = false));
            },

            /**
             * Handle products response
             *
             * @param {Object} response
             */
            receiveProductSelections(response) {
                this.productSelections = response.data
                    .filter((selection, i) => i === response.data.length - 1)
                    .map((selection) => {
                        selection.products = selection.products.map((product) => {
                            product.selected_options = [];

                            product.options.forEach((option) => {
                                product.selected_options.push({
                                    id: option.id,
                                    value_id: this.getValueId(product, option.id)
                                });
                            });

                            return product;
                        });

                        return selection;
                    });
            },

            /**
             * Try to find a value ID for a given product option
             *
             * @returns {Number|null}
             */
            getValueId(product, option_id) {
                if (!product.variant_id) {
                    return null;
                }

                const variant = find(product.variants, { id: product.variant_id });

                if (!variant) {
                    return null;
                }

                const value = find(variant.option_values, { option_id });

                if (value) {
                    return value.id;
                }

                return null;
            },

            /**
             * Handle products' "edit" event
             */
            handleProductsEdit() {
                this.editingProducts = true;
            },

            /**
             * Handle products' "cancel" event
             */
            handleProductsCancel() {
                this.editingProducts = false;
                this.selectedProducts = [];
            },

            /**
             * Handle products' "change" event
             *
             * @param {Array} products
             */
            handleProductsChange(products) {
                this.selectedProducts = products;
            },

            /**
             * Handle products' "save" event
             *
             * @type {Number} selectionId
             */
            handleProductsSave(selectionId) {
                this.savingProducts = true;

                this.$http
                    .post(`/campaigns/selection/${selectionId}/products/save`, { products: this.selectedProducts })
                    .then((response) => {
                        this.receiveProductSelections(response);

                        this.handleProductsCancel();
                    })
                    .catch((response) => this.$requestErrors(response))
                    .then(() => (this.savingProducts = false));
            }
        }
    });
</script>

<style>
    .deal-payment-schedule-table tr:last-child td {
        border: none;
    }
</style>
